import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { users } from '@/api/endpoints';
import { Notice } from '@/components/Notice';
import { RequirePermission } from '@/components/RequirePermission';
import { describe } from '@/lib/errors';
import { formatDateTime } from '@/lib/format';
import type { Role } from '@/api/types';

const ROLES: Role[] = ['SUPER_ADMIN', 'ADMIN', 'OPERATOR', 'READ_ONLY'];

const ROLE_SUMMARY: Record<Role, string> = {
  SUPER_ADMIN: 'everything, including credentials and live trading',
  ADMIN: 'configure strategies and tighten risk limits',
  OPERATOR: 'start, pause and emergency-stop trading',
  READ_ONLY: 'view only',
};

export function UsersPage() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('READ_ONLY');
  const [message, setMessage] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);

  const list = useQuery({ queryKey: ['users'], queryFn: users.list });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['users'] });

  const create = useMutation({
    mutationFn: () => users.create({ email, password, role }),
    onSuccess: () => {
      setMessage({ tone: 'info', text: `Added ${email}.` });
      setEmail('');
      setPassword('');
      refresh();
    },
    onError: (error) => setMessage({ tone: 'error', text: describe(error) }),
  });

  const update = useMutation({
    mutationFn: ({
      publicId,
      body,
    }: {
      publicId: string;
      body: { is_active?: boolean; role?: string };
    }) => users.update(publicId, body),
    onSuccess: refresh,
    onError: (error) => setMessage({ tone: 'error', text: describe(error) }),
  });

  const unlock = useMutation({
    mutationFn: (publicId: string) => users.unlock(publicId),
    onSuccess: refresh,
    onError: (error) => setMessage({ tone: 'error', text: describe(error) }),
  });

  return (
    <>
      <div className="page-head">
        <h1>People</h1>
        <p>
          Changing someone's role or turning off their account signs them out immediately, rather
          than when their session would have expired.
        </p>
      </div>

      {message && <Notice tone={message.tone}>{message.text}</Notice>}

      <section className="panel">
        <div className="panel-body flush">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Two-factor</th>
                <th>Status</th>
                <th>Last signed in</th>
                <th className="right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((user) => (
                <tr key={user.public_id}>
                  <td>{user.email}</td>
                  <td>
                    <RequirePermission
                      permission="danger:users"
                      fallback={<span>{user.role.replace('_', ' ').toLowerCase()}</span>}
                    >
                      <select
                        value={user.role}
                        aria-label={`Role for ${user.email}`}
                        style={{ maxWidth: 160 }}
                        onChange={(event) =>
                          update.mutate({
                            publicId: user.public_id,
                            body: { role: event.target.value },
                          })
                        }
                      >
                        {ROLES.map((value) => (
                          <option key={value} value={value}>
                            {value.replace('_', ' ').toLowerCase()}
                          </option>
                        ))}
                      </select>
                    </RequirePermission>
                  </td>
                  <td style={{ color: user.mfa_enabled ? undefined : 'var(--warn)' }}>
                    {user.mfa_enabled ? 'on' : 'off'}
                  </td>
                  <td>{!user.is_active ? 'turned off' : user.locked_until ? 'locked' : 'active'}</td>
                  <td className="num">{formatDateTime(user.last_login_at)}</td>
                  <td className="right">
                    <RequirePermission permission="danger:users">
                      {user.locked_until && (
                        <button type="button" onClick={() => unlock.mutate(user.public_id)}>
                          Unlock
                        </button>
                      )}{' '}
                      <button
                        type="button"
                        className={user.is_active ? 'danger' : ''}
                        onClick={() =>
                          update.mutate({
                            publicId: user.public_id,
                            body: { is_active: !user.is_active },
                          })
                        }
                      >
                        {user.is_active ? 'Turn off' : 'Turn on'}
                      </button>
                    </RequirePermission>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <RequirePermission permission="danger:users">
        <section className="panel">
          <div className="panel-head">
            <h2>Add someone</h2>
          </div>
          <div
            className="panel-body"
            style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 420 }}
          >
            <div>
              <label htmlFor="new-email">Email</label>
              <input
                id="new-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <div>
              <label htmlFor="new-password">Temporary password</label>
              <input
                id="new-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <p className="hint" style={{ marginTop: 'var(--space-2)' }}>
                At least 14 characters. They can change it after signing in.
              </p>
            </div>
            <div>
              <label htmlFor="new-role">Role</label>
              <select
                id="new-role"
                value={role}
                onChange={(event) => setRole(event.target.value as Role)}
              >
                {ROLES.map((value) => (
                  <option key={value} value={value}>
                    {value.replace('_', ' ').toLowerCase()} — {ROLE_SUMMARY[value]}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="primary"
              style={{ justifySelf: 'start' }}
              disabled={create.isPending || !email || !password}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Adding…' : 'Add person'}
            </button>
          </div>
        </section>
      </RequirePermission>
    </>
  );
}
